import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  History,
  Loader2,
  Search,
  Filter,
  RefreshCw,
  User,
  Clock,
  FileText,
  Briefcase,
  Truck,
  Wrench,
  FolderKanban,
  Package,
  Building2,
  Users,
  ChevronDown,
  ChevronRight,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ENTITY_TYPES = [
  { value: "all", label: "All Types", icon: FileText },
  { value: "contractor", label: "Contractors", icon: Briefcase },
  { value: "supplier", label: "Suppliers", icon: Truck },
  { value: "maintenance", label: "Maintenance", icon: Wrench },
  { value: "project", label: "Projects", icon: FolderKanban },
  { value: "asset", label: "Assets", icon: Package },
  { value: "health_system", label: "Health Systems", icon: Building2 },
  { value: "hospital_user", label: "Hospital Users", icon: Users },
];

const ACTION_TYPES = [
  { value: "all", label: "All Actions" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "invite", label: "Invite" },
  { value: "resend_setup_link", label: "Resend Link" },
];

const getEntityIcon = (entityType) => {
  const entity = ENTITY_TYPES.find(e => e.value === entityType);
  return entity?.icon || FileText;
};

const getActionColor = (action) => {
  switch(action) {
    case 'create': return 'bg-emerald-100 text-emerald-700';
    case 'update': return 'bg-blue-100 text-blue-700';
    case 'delete': return 'bg-red-100 text-red-700';
    case 'invite': return 'bg-purple-100 text-purple-700';
    case 'resend_setup_link': return 'bg-amber-100 text-amber-700';
    default: return 'bg-slate-100 text-slate-700';
  }
};

const getActionEmoji = (action) => {
  switch(action) {
    case 'create': return '✨';
    case 'update': return '✏️';
    case 'delete': return '🗑️';
    case 'invite': return '📨';
    case 'resend_setup_link': return '🔗';
    default: return '📋';
  }
};

export default function AuditLogDashboard() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [entityType, setEntityType] = useState("all");
  const [actionType, setActionType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [limit, setLimit] = useState(100);
  
  // Detail modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [entityType, actionType, limit]);

  const fetchLogs = async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (entityType !== "all") params.append("entity_type", entityType);
      if (actionType !== "all") params.append("action", actionType);
      if (searchTerm) params.append("performed_by", searchTerm);
      params.append("limit", limit.toString());
      
      const res = await fetch(`${API}/admin/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (err) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSearch = () => {
    fetchLogs(true);
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    return (
      log.performed_by_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.entity_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details?.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details?.contractor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details?.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details?.project_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff/60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)} hours ago`;
    if (diff < 604800000) return `${Math.floor(diff/86400000)} days ago`;
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  const getEntityName = (log) => {
    const details = log.details || {};
    return details.user_name || 
           details.contractor_name || 
           details.supplier_name || 
           details.project_name || 
           details.asset_name ||
           details.health_system_name ||
           log.entity_id;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <History className="w-6 h-6 text-blue-600" />
            Audit Log
          </h2>
          <p className="text-slate-500 text-sm">Track all changes made to system records</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => fetchLogs(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-slate-600 text-sm">Search</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by user, entity name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="w-48">
            <Label className="text-slate-600 text-sm">Entity Type</Label>
            <Select value={entityType} onValueChange={setEntityType}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="w-40">
            <Label className="text-slate-600 text-sm">Action</Label>
            <Select value={actionType} onValueChange={setActionType}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACTION_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="w-32">
            <Label className="text-slate-600 text-sm">Limit</Label>
            <Select value={limit.toString()} onValueChange={(v) => setLimit(parseInt(v))}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="250">250</SelectItem>
                <SelectItem value="500">500</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700">
            <Filter className="w-4 h-4 mr-2" />
            Apply
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-sm text-slate-500">Total Logs</p>
          <p className="text-2xl font-bold text-slate-900">{filteredLogs.length}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-sm text-emerald-600">Creates</p>
          <p className="text-2xl font-bold text-emerald-700">
            {filteredLogs.filter(l => l.action === 'create').length}
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-600">Updates</p>
          <p className="text-2xl font-bold text-blue-700">
            {filteredLogs.filter(l => l.action === 'update').length}
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-600">Deletes</p>
          <p className="text-2xl font-bold text-red-700">
            {filteredLogs.filter(l => l.action === 'delete').length}
          </p>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Action</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Entity</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Performed By</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Time</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredLogs.map(log => {
              const EntityIcon = getEntityIcon(log.entity_type);
              return (
                <tr key={log.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                      {getActionEmoji(log.action)} {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <EntityIcon className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-900 text-sm">
                          {log.entity_type?.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-slate-500">{getEntityName(log)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
                        <User className="w-3 h-3 text-slate-600" />
                      </div>
                      <span className="text-sm text-slate-600">{log.performed_by_email || 'System'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(log.timestamp)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => { setSelectedLog(log); setShowDetail(true); }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
            {filteredLogs.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                  <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No audit logs found</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Audit Log Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500 text-xs">Action</Label>
                    <p className="font-medium">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getActionColor(selectedLog.action)}`}>
                        {getActionEmoji(selectedLog.action)} {selectedLog.action}
                      </span>
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Entity Type</Label>
                    <p className="font-medium text-slate-900">{selectedLog.entity_type?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Entity ID</Label>
                    <code className="text-xs bg-slate-200 px-2 py-1 rounded">{selectedLog.entity_id}</code>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Timestamp</Label>
                    <p className="font-medium text-slate-900">
                      {new Date(selectedLog.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-500 text-xs">Performed By</Label>
                    <p className="font-medium text-slate-900">{selectedLog.performed_by_email || 'System'}</p>
                  </div>
                </div>
              </div>

              {/* Changes */}
              {selectedLog.details?.changes && Object.keys(selectedLog.details.changes).length > 0 && (
                <div>
                  <Label className="text-slate-700 font-medium">Changes Made</Label>
                  <div className="mt-2 space-y-2">
                    {Object.entries(selectedLog.details.changes).map(([field, change]) => (
                      <div key={field} className="bg-white border border-slate-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-slate-700 mb-2">{field.replace('_', ' ')}</p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="bg-red-50 rounded p-2">
                            <p className="text-xs text-red-600 mb-1">Before</p>
                            <p className="text-red-700 break-all">
                              {change.old !== null && change.old !== undefined ? String(change.old) : '(empty)'}
                            </p>
                          </div>
                          <div className="bg-emerald-50 rounded p-2">
                            <p className="text-xs text-emerald-600 mb-1">After</p>
                            <p className="text-emerald-700 break-all">
                              {change.new !== null && change.new !== undefined ? String(change.new) : '(empty)'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deleted Data Preview (for delete actions) */}
              {selectedLog.action === 'delete' && selectedLog.details?.deleted_data && (
                <div>
                  <Label className="text-slate-700 font-medium">Deleted Record Data</Label>
                  <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                    <pre className="text-xs text-red-800 whitespace-pre-wrap">
                      {JSON.stringify(selectedLog.details.deleted_data, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Raw Details */}
              <div>
                <Label className="text-slate-700 font-medium">Full Details (JSON)</Label>
                <div className="mt-2 bg-slate-900 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <pre className="text-xs text-emerald-400 whitespace-pre-wrap">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
